import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { QueryDropdown } from 'basic-components';
import { Query } from 'dashboard-services';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';

import DropdownIndicator from './DropdownIndicator';

import './UserDefinedFilter.scss';

const dropdownComponents = {
  DropdownIndicator
}

const useSearchSymbolGroups = ({ query, groupName, symbolsFilter, metadatasFilter, source }) => {
  const dispatch = useDispatch(),
        buildedQuery = creatorSeriesActions.getSearchQuery({ query, symbolsFilter, metadatasFilter, groupName }),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withHeader("Target", source, true)
        , [dispatch, source]),
        select = useCallback(data => data?.pages?.flatMap(page => page?.items)?.map(item => ({ value: item.groupName, label: item.groupName })), []),
        { data: symbolGroups, isFetching, fetchNextPage, isFetchingNextPage } = dataStorageQueries.useSearchSymbols({
          getQueryBuilder,
          query: buildedQuery,
          collapse: "groupName", 
          sortBy: "groupName.keyword=asc",
          withValuesOnly: true,
          shouldRefetch: false,
          select,
          provider: source
        })

  return { symbolGroups, isFetching, isFetchingNextPage, fetchNextPage };
}

const UserDefinedGroupFilter = ({ filterKey, label, value, setValue, symbols, metadatas, groupFilter, index }) => {
  const [inputQuery, setInputQuery] = useState(""),
        source = useSelector(state => state.globalState.source),
        { 
          symbolGroups,
          isFetching,
          isFetchingNextPage, 
          fetchNextPage,
        } = useSearchSymbolGroups({ 
          query: undefined,  // other dropdowns does not support it
          groupName: inputQuery,
          symbolsFilter: symbols, 
          metadatasFilter: metadatas,
          source
        }),
        onChange = useCallback(value => setValue({ value, name: filterKey }), [filterKey, setValue]),
        dropdownRef = useRef()

  useEffect(() => {
    dropdownRef.current?.refreshOptions?.()
  }, [groupFilter, metadatas, symbols, source])

  return (
    <div className="user-defined-filter">
      <QueryDropdown
          className="user-defined-filter__dropdown"
          components={dropdownComponents}
          creatable
          defaultValue={value}
          fetchNextPage={fetchNextPage}
          fixed
          isFetching={isFetching || isFetchingNextPage}
          key={index + filterKey}
          menuPlacement="bottom"
          menuPortalTarget={document.querySelector('.ng-office-app__authed__content--series')}
          name={filterKey}
          noError
          onChange={onChange}
          onInputChange={setInputQuery}
          options={symbolGroups}
          placeholder={label}
          ref={dropdownRef}
          variant="border"
      />
    </div>
  )    
}

UserDefinedGroupFilter.defaultProps = {
  groupFilter: undefined,
  metadatas: undefined,
  symbols: undefined,
  value: undefined
}

UserDefinedGroupFilter.propTypes = {
  filterKey: PropTypes.string.isRequired,
  groupFilter: PropTypes.string,
    index: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  metadatas: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  symbols: PropTypes.object,
  value: PropTypes.string
}

export default UserDefinedGroupFilter;